import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { DataTable, formatDateValue } from "../../components/DataTable";
import type { JsonRecord } from "../../types";
import type { ExecutionTypeConfig } from "./executionTypes";

export function ExecutionTable({
  config,
  rows,
  onView,
  onEdit,
  onDelete,
  onValidate,
}: {
  config: ExecutionTypeConfig;
  rows: JsonRecord[];
  onView: (row: JsonRecord) => void;
  onEdit: (row: JsonRecord) => void;
  onDelete: (row: JsonRecord) => void;
  onValidate: (row: JsonRecord) => void;
}) {
  const [openMenu, setOpenMenu] = useState<{ id: string; rect: DOMRect } | null>(null);

  useEffect(() => {
    const closeOnOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".execution-action-menu")) setOpenMenu(null);
    };
    const closeOnViewportChange = () => setOpenMenu(null);

    document.addEventListener("mousedown", closeOnOutside);
    window.addEventListener("resize", closeOnViewportChange);
    window.addEventListener("scroll", closeOnViewportChange, true);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      window.removeEventListener("resize", closeOnViewportChange);
      window.removeEventListener("scroll", closeOnViewportChange, true);
    };
  }, []);

  const closeAnd = (callback: () => void) => {
    setOpenMenu(null);
    callback();
  };

  return <DataTable
    rows={rows}
    paginate={false}
    columns={config.columns.map((column) => column.key)}
    columnLabels={Object.fromEntries(config.columns.map((column) => [column.key, column.label]))}
    tableClassName={`execution-table execution-table-${config.key}`}
    renderCell={(row, key) => {
      if (key !== "fecha" && key !== "fecha_termino" && key !== "fecha_suministro") return undefined;
      return <span style={{ whiteSpace: "nowrap" }}>{formatDateValue(row[key]) || String(row[key] ?? "—")}</span>;
    }}
    actions={(row) => {
      const id = String(row.id ?? "");
      const open = openMenu?.id === id;
      const steelSupply = config.key === "acero_estructural" || config.key === "acero_refuerzo";
      const menuHeight = steelSupply || row.puede_validar === true ? 174 : 136;
      const openUp = open && openMenu.rect.bottom + menuHeight + 8 > window.innerHeight
        && openMenu.rect.top > menuHeight + 8;

      return <div className="execution-action-menu" style={{ position: "relative" }}>
        <button
          type="button"
          className="icon-button"
          aria-label="Acciones"
          title="Acciones"
          aria-expanded={open}
          style={{ width: 32, height: 32 }}
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            setOpenMenu((current) => current?.id === id ? null : { id, rect });
          }}
        >
          <MoreHorizontal size={18} />
        </button>
        {open && createPortal(<div className="execution-action-menu" style={{
          position: "fixed",
          zIndex: 1000,
          top: openUp ? undefined : openMenu.rect.bottom + 5,
          bottom: openUp ? window.innerHeight - openMenu.rect.top + 5 : undefined,
          left: Math.max(8, Math.min(openMenu.rect.right - 150, window.innerWidth - 158)),
          width: 150,
          padding: 5,
          border: "1px solid var(--line)",
          borderRadius: 8,
          background: "white",
          boxShadow: "0 10px 25px #17312b22",
          display: "grid",
          gap: 2,
        }}>
          <button
            type="button"
            className="button-link"
            style={{ padding: "9px 10px", textAlign: "left", borderRadius: 6 }}
            onClick={() => closeAnd(() => onView(row))}
          >
            {steelSupply ? "Datos adicionales" : "Detalles"}
          </button>
          <button
            type="button"
            className="button-link"
            style={{ padding: "9px 10px", textAlign: "left", borderRadius: 6 }}
            disabled={steelSupply ? row.puede_editar !== true : row.puede_editar === false}
            title={(steelSupply ? row.puede_editar !== true : row.puede_editar === false) ? "No tienes permiso para editar" : "Editar"}
            onClick={() => closeAnd(() => onEdit(row))}
          >
            Editar
          </button>
          <button
            type="button"
            className="button-link"
            style={{ padding: "9px 10px", textAlign: "left", borderRadius: 6, color: "#b42318" }}
            disabled={row.puede_eliminar !== true}
            title={row.puede_eliminar === true ? "Eliminar" : "No tienes permiso para eliminar"}
            onClick={() => closeAnd(() => onDelete(row))}
          >
            Eliminar
          </button>
          {steelSupply && <span title="Funcionalidad de calidad pendiente por definir" style={{ display: "grid" }}><button
            type="button"
            className="button-link"
            style={{ padding: "9px 10px", textAlign: "left", borderRadius: 6 }}
            disabled
          >
            Calidad
          </button></span>}
          {!steelSupply && row.puede_validar === true && <button
            type="button"
            className="button-link"
            style={{ padding: "9px 10px", textAlign: "left", borderRadius: 6, color: "#22634f", fontWeight: 700 }}
            onClick={() => closeAnd(() => onValidate(row))}
          >
            Validar
          </button>}
        </div>, document.body)}
      </div>;
    }}
  />;
}
